// Executive Challenge bottom sheets: enter-code (join), verify identity
// (phone → SMS code → player ID), and challenge-created (host). One module —
// they share the sheet frame, the segmented-code input, and the design tokens.
//
// Phase 1: identity is a local stub. The verify flow renders and behaves like
// the real thing (so Phase 2 is a backend swap, not a UI rework), but any
// phone/OTP input passes, and the player ID persists in localStorage.

import {
	useEffect,
	useRef,
	useState,
	type KeyboardEvent,
	type ReactNode,
} from "react";
import {
	CHALLENGE_CODE_LENGTH,
	normalizePlayerId,
} from "../../common/executiveChallenge.ts";
import {
	getStoredPlayerId,
	storeVerifiedIdentity,
} from "../util/challengeUi.ts";
import { toWorker } from "../util/toWorker.ts";

// ---------------------------------------------------------------- frame

export const ChallengeSheet = ({
	onClose,
	children,
	// Sheets whose inputs auto-focus (join code, verify) open the on-screen
	// keyboard immediately; a bottom sheet would sit behind it. Anchoring them
	// to the top keeps the code/ID cells visible above the keyboard. Sheets
	// with no input (created) stay as bottom sheets.
	anchor = "bottom",
}: {
	onClose: () => void;
	children: ReactNode;
	anchor?: "top" | "bottom";
}) => (
	<div
		className={`chs-backdrop ${anchor === "top" ? "chs-top" : ""}`}
		onClick={onClose}
	>
		<style>{styles}</style>
		<div className="chs-sheet" onClick={(e) => e.stopPropagation()}>
			<div className="chs-handle" />
			{children}
		</div>
	</div>
);

// Segmented character boxes driven by a hidden input, so the native keyboard
// (numeric for codes) does the typing and the boxes are pure presentation.
const SegmentedInput = ({
	length,
	value,
	onChange,
	onEnter,
	numeric,
	error,
	big,
}: {
	length: number;
	value: string;
	onChange: (value: string) => void;
	onEnter?: () => void;
	numeric?: boolean;
	error?: boolean;
	big?: boolean;
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter" && onEnter) {
			e.preventDefault();
			onEnter();
		}
	};

	const cells = Array.from({ length }, (_, i) => {
		const ch = value[i] ?? "";
		const active = i === value.length && value.length < length;
		const filled = i < value.length;
		return (
			<div
				key={i}
				className={`chs-cell ${big ? "big" : ""} ${
					error && filled ? "err" : filled ? "filled" : active ? "active" : ""
				}`}
			>
				{ch}
				{active ? <span className="chs-caret" /> : null}
			</div>
		);
	});

	return (
		<div className="chs-cells" onClick={() => inputRef.current?.focus()}>
			{cells}
			<input
				ref={inputRef}
				className="chs-hidden-input"
				inputMode={numeric ? "numeric" : "text"}
				autoCapitalize="characters"
				autoCorrect="off"
				spellCheck={false}
				value={value}
				onChange={(e) => {
					const raw = e.target.value;
					const cleaned = numeric
						? raw.replaceAll(/\D/g, "")
						: normalizePlayerId(raw);
					onChange(cleaned.slice(0, length));
				}}
				onKeyDown={onKeyDown}
			/>
		</div>
	);
};

// ---------------------------------------------------------------- join

export const ChallengeJoinSheet = ({
	onClose,
	onContinue,
	resolveCode,
	teamNameForTid,
	initialCode,
}: {
	onClose: () => void;
	// The code was found and confirmed — proceed to verify.
	onContinue: (code: string) => void;
	// Worker lookup: code -> tid. Throws on a bad code.
	resolveCode: (code: string) => Promise<{ tid: number }>;
	teamNameForTid: (tid: number) => string | undefined;
	// Prefill from a /c/{code} deep link — the user still confirms via Find.
	initialCode?: string;
}) => {
	const [code, setCode] = useState(initialCode ?? "");
	const [confirmTid, setConfirmTid] = useState<number | undefined>(undefined);
	const [error, setError] = useState("");
	const [finding, setFinding] = useState(false);

	const find = async () => {
		if (code.length !== CHALLENGE_CODE_LENGTH || finding) {
			return;
		}
		setFinding(true);
		setError("");
		try {
			const { tid } = await resolveCode(code);
			setConfirmTid(tid);
		} catch (error_) {
			// The worker's message carries the specific reason (malformed code,
			// closed challenge, …); the generic copy is only a fallback.
			setError((error_ as Error).message || "No challenge with that code.");
		} finally {
			setFinding(false);
		}
	};

	const teamName =
		confirmTid !== undefined ? teamNameForTid(confirmTid) : undefined;

	return (
		<ChallengeSheet onClose={onClose} anchor="top">
			<div className="chs-badge">🎟</div>
			<div className="chs-title">JOIN A CHALLENGE</div>
			<div className="chs-sub">Enter the 6-digit code from your host.</div>
			<SegmentedInput
				length={CHALLENGE_CODE_LENGTH}
				value={code}
				onChange={(v) => {
					setCode(v);
					setConfirmTid(undefined);
					setError("");
				}}
				onEnter={find}
				numeric
			/>
			{error ? <div className="chs-error">{error}</div> : null}
			{confirmTid !== undefined ? (
				<>
					<div className="chs-note">
						You’ll manage the <b>{teamName ?? "host’s team"}</b> — the same
						world as your group.
					</div>
					<button
						type="button"
						className="chs-cta"
						onClick={() => onContinue(code)}
					>
						Continue →
					</button>
				</>
			) : (
				<button
					type="button"
					className="chs-cta"
					disabled={code.length !== CHALLENGE_CODE_LENGTH || finding}
					onClick={find}
				>
					{finding ? "Finding…" : "Find Challenge"}
				</button>
			)}
		</ChallengeSheet>
	);
};

// ---------------------------------------------------------------- verify

export const ChallengeVerify = ({
	onClose,
	onVerified,
}: {
	onClose: () => void;
	// Identity locked in — the caller fires createChallenge / joinChallenge.
	onVerified: (playerId: string) => void;
}) => {
	// There is NO phone requirement: every mode goes straight to the initials
	// picker. The phone → OTP steps exist only for the future launch config
	// (Supabase phone auth, identity.sms) — the one mode where the board's
	// one-entry-per-person rule is enforced by a real SMS round-trip.
	const [identity, setIdentity] = useState<{
		mode: "local" | "supabase";
		sms: boolean;
	}>({ mode: "local", sms: false });
	const [step, setStep] = useState<"phone" | "otp" | "id">("id");
	const [phone, setPhone] = useState("");
	const [otp, setOtp] = useState("");
	const [otpError, setOtpError] = useState(false);
	const [sendError, setSendError] = useState("");
	// An OTP send or verify is in flight — the design's "Sending…"/"Verifying…"
	// loading CTAs, and a double-tap guard on real SMS sends (they cost money).
	const [busy, setBusy] = useState(false);
	const [playerId, setPlayerId] = useState(getStoredPlayerId());

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const id = await toWorker("main", "getChallengeIdentity", undefined);
			if (cancelled) {
				return;
			}
			setIdentity({ mode: id.mode, sms: id.sms });
			if (id.mode === "supabase") {
				// The identity's saved initials beat the device's stale copy.
				if (id.playerId) {
					setPlayerId(normalizePlayerId(id.playerId));
				}
				if (id.sms && !id.verified) {
					// The one gated mode: phone auth configured and no live session.
					setStep("phone");
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	// US-only for now (the country pill is fixed at +1); Supabase Auth wants
	// E.164.
	const phoneE164 = `+1${phone.replaceAll(/\D/g, "")}`;

	// Resend countdown (design §4b): "Resend code in 0:30" ticking to 0, then a
	// tappable "Resend code" that re-fires the send.
	const RESEND_SECONDS = 30;
	const [resendIn, setResendIn] = useState(RESEND_SECONDS);
	useEffect(() => {
		if (step !== "otp") {
			return;
		}
		const interval = setInterval(() => {
			// Idle at 0 rather than clearing — simpler than a resendIn dependency,
			// and the no-op tick is free.
			setResendIn((s) => (s > 0 ? s - 1 : s));
		}, 1000);
		return () => clearInterval(interval);
	}, [step]);

	const sendCode = async () => {
		if (busy) {
			return;
		}
		setSendError("");
		if (identity.mode === "supabase" && identity.sms) {
			setBusy(true);
			try {
				await toWorker("main", "requestChallengeOtp", { phone: phoneE164 });
			} catch (error) {
				setSendError(
					(error as Error).message ||
						"Couldn’t send the code — check the number and try again.",
				);
				setBusy(false);
				return;
			}
			setBusy(false);
		}
		// Fresh timer, clean slate (stub mode "sends" instantly).
		setResendIn(RESEND_SECONDS);
		setOtp("");
		setOtpError(false);
		setStep("otp");
	};

	const verifyOtp = async () => {
		if (otp.length !== 6 || busy) {
			return;
		}
		if (identity.mode === "supabase" && identity.sms) {
			setBusy(true);
			try {
				await toWorker("main", "verifyChallengeOtp", {
					phone: phoneE164,
					token: otp,
				});
			} catch {
				setOtpError(true);
				setBusy(false);
				return;
			}
			setBusy(false);
		} else if (/^(\d)\1{5}$/.test(otp)) {
			// Stub: any code passes EXCEPT an all-same-digit one (000000, 111111,
			// …) — so the error state is reachable before the real check exists.
			setOtpError(true);
			return;
		}
		setStep("id");
	};

	// Focus the phone field on open (an effect, not autoFocus, per a11y lint).
	const phoneRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (step === "phone") {
			phoneRef.current?.focus();
		}
	}, [step]);

	const lockIn = () => {
		if (playerId.length === 0) {
			return;
		}
		// Device copy either way (stub skip logic + instant prefill), plus a
		// best-effort pin to the verified identity's metadata so the initials
		// follow the phone across devices. Fire-and-forget: a metadata failure
		// must never block entering a challenge.
		storeVerifiedIdentity(playerId);
		if (identity.mode === "supabase") {
			toWorker("main", "setChallengePlayerId", { playerId });
		}
		onVerified(playerId);
	};

	return (
		<ChallengeSheet onClose={onClose} anchor="top">
			{step === "phone" ? (
				<>
					<div className="chs-badge">📱</div>
					<div className="chs-title">VERIFY TO COMPETE</div>
					<div className="chs-sub">
						We text you a code to keep the board fair — one executive, one
						entry.
					</div>
					<div className="chs-phone-row">
						<div className="chs-country">🇺🇸 +1</div>
						<input
							ref={phoneRef}
							className="chs-phone-input"
							inputMode="tel"
							placeholder="(555) 123-4567"
							value={phone}
							onChange={(e) =>
								setPhone(
									e.target.value.replaceAll(/[^\d ()-]/g, "").slice(0, 14),
								)
							}
						/>
					</div>
					<div className="chs-consent">
						By continuing you agree to receive a one-time SMS. Msg &amp; data
						rates apply.
					</div>
					{sendError ? <div className="chs-error">{sendError}</div> : null}
					<div className="chs-btn-row">
						<button
							type="button"
							className="chs-cta"
							disabled={phone.replaceAll(/\D/g, "").length < 10 || busy}
							onClick={sendCode}
						>
							{busy ? "Sending…" : "Send code"}
						</button>
						<button type="button" className="chs-ghost" onClick={onClose}>
							Cancel
						</button>
					</div>
				</>
			) : null}

			{step === "otp" ? (
				<>
					<div className="chs-title" style={{ marginTop: 20 }}>
						ENTER YOUR CODE
					</div>
					<div className="chs-sub">
						Sent to +1 {phone || "your phone"} ·{" "}
						<button
							type="button"
							className="chs-inline-link"
							onClick={() => setStep("phone")}
						>
							Edit
						</button>
					</div>
					<SegmentedInput
						length={6}
						value={otp}
						onChange={(v) => {
							setOtp(v);
							setOtpError(false);
						}}
						onEnter={verifyOtp}
						numeric
						error={otpError}
					/>
					{otpError ? (
						<div className="chs-error">
							That code didn’t match. Check it and try again.
						</div>
					) : null}
					<div className="chs-resend">
						{resendIn > 0 ? (
							`Resend code in 0:${String(resendIn).padStart(2, "0")}`
						) : (
							<button
								type="button"
								className="chs-inline-link"
								onClick={sendCode}
							>
								Resend code
							</button>
						)}
					</div>
					<button
						type="button"
						className="chs-cta"
						disabled={otp.length !== 6 || busy}
						onClick={verifyOtp}
					>
						{busy ? "Verifying…" : "Verify"}
					</button>
				</>
			) : null}

			{step === "id" ? (
				<>
					<div className="chs-badge">🏀</div>
					{identity.sms ? (
						// Only meaningful when a real phone check stands behind it.
						<div className="chs-verified">YOU’RE VERIFIED ✓</div>
					) : null}
					<div className="chs-title-lg">PICK YOUR PLAYER ID</div>
					<div className="chs-sub">Your initials on the leaderboard.</div>
					<SegmentedInput
						length={4}
						value={playerId}
						onChange={setPlayerId}
						onEnter={lockIn}
						big
					/>
					<div className="chs-hint">Up to 4 characters — A–Z, 0–9.</div>
					<button
						type="button"
						className="chs-cta"
						disabled={playerId.length === 0}
						onClick={lockIn}
					>
						Lock it in
					</button>
				</>
			) : null}
		</ChallengeSheet>
	);
};

// ---------------------------------------------------------------- created

export const ChallengeCreatedSheet = ({
	code,
	onStart,
	onCopy,
}: {
	code: string;
	onStart: () => void;
	onCopy: () => void;
}) => (
	// Celebratory, and deliberately not dismissable by backdrop tap alone into
	// nowhere — closing it also just starts managing.
	<ChallengeSheet onClose={onStart}>
		<div className="chs-badge chs-pop">🏆</div>
		<div className="chs-title">CHALLENGE CREATED</div>
		<div className="chs-sub">
			Share this code — everyone plays the same team and the same season.
		</div>
		<div className="chs-code-hero">
			<div className="chs-spectrum" />
			<div className="chs-code-hero-code">{code}</div>
		</div>
		<button type="button" className="chs-cta" onClick={onCopy}>
			📋 Copy invite code
		</button>
		<button type="button" className="chs-link" onClick={onStart}>
			Start managing ›
		</button>
	</ChallengeSheet>
);

// ---------------------------------------------------------------- styles

const styles = `
.chs-backdrop { position:fixed; inset:0; z-index:2100; background:rgba(0,0,0,.66);
  display:flex; align-items:flex-end; justify-content:center; animation:chsFade .2s ease both; }
.chs-sheet { width:100%; max-width:520px; background:#181818; border:1px solid #2a2a2a;
  border-radius:22px 22px 0 0; padding:12px 20px calc(22px + env(safe-area-inset-bottom));
  animation:chsUp .24s cubic-bezier(.2,.9,.3,1) both;
  font-family:Montserrat,system-ui,sans-serif; color:#fff; }
/* Top-anchored variant: the sheet drops from the top so its inputs stay above
   the on-screen keyboard. It also scrolls if it's taller than the space left. */
.chs-backdrop.chs-top { align-items:flex-start; }
.chs-backdrop.chs-top .chs-sheet { border-radius:0 0 22px 22px;
  padding:calc(12px + env(safe-area-inset-top)) 20px 22px;
  max-height:100%; overflow-y:auto; animation-name:chsDown; }
.chs-backdrop.chs-top .chs-handle { display:none; }
.chs-handle { width:38px; height:4px; border-radius:3px; background:#3a3a3a; margin:0 auto 6px; }
.chs-badge { width:52px; height:52px; border-radius:50%; background:rgba(24,224,168,.16);
  border:1px solid rgba(24,224,168,.4); display:flex; align-items:center; justify-content:center;
  font-size:23px; margin:8px auto 0; }
.chs-pop { animation:chsPop .5s cubic-bezier(.2,.9,.3,1.2) both; }
.chs-title { text-align:center; font-size:15px; font-weight:900; letter-spacing:.4px; margin-top:12px; }
.chs-title-lg { text-align:center; font-size:19px; font-weight:900; letter-spacing:.3px; margin-top:4px; }
.chs-verified { text-align:center; font-size:11px; font-weight:800; letter-spacing:.6px;
  color:#18E0A8; margin-top:12px; }
.chs-sub { text-align:center; font-size:12.5px; color:#9aa4ac; font-weight:600; line-height:1.5;
  max-width:300px; margin:6px auto 0; }
.chs-hint { text-align:center; font-size:11px; color:#7f8b93; font-weight:600; margin-top:12px; }
.chs-error { text-align:center; font-size:11.5px; font-weight:700; color:#E2504E; margin-top:12px; }
.chs-resend { text-align:center; font-size:11.5px; font-weight:700; color:#7f8b93; margin-top:12px; }
.chs-note { margin-top:16px; background:rgba(24,224,168,.08); border:1px solid rgba(24,224,168,.28);
  border-radius:12px; padding:11px 13px; font-size:12px; font-weight:600; color:#cfe0ec; line-height:1.45; }
.chs-note b { color:#fff; }
.chs-inline-link { background:none; border:none; padding:0; color:#18E0A8; font-weight:800;
  font-size:12px; font-family:inherit; cursor:pointer; }

.chs-cells { position:relative; display:flex; gap:8px; justify-content:center; margin-top:18px; cursor:text; }
.chs-hidden-input { position:absolute; inset:0; opacity:0; border:none; background:transparent;
  color:transparent; caret-color:transparent; width:100%; font-size:16px; }
.chs-cell { width:44px; height:56px; border-radius:12px; display:flex; align-items:center;
  justify-content:center; font-size:26px; font-weight:900; color:#fff;
  background:#161616; border:1px solid #232323; }
.chs-cell.filled { background:#1c1c1c; border:1px solid #2a2a2a; }
.chs-cell.err { background:rgba(226,80,78,.1); border:1px solid #E2504E; color:#E2504E; }
.chs-cell.active { background:#1c1c1c; border:2px solid #18E0A8;
  box-shadow:0 0 0 4px rgba(24,224,168,.14); }
.chs-cell.big { width:64px; height:80px; border-radius:14px; font-size:44px; }
.chs-cell.big.filled, .chs-cell.big.active { background:rgba(24,224,168,.08); border:2px solid #18E0A8;
  box-shadow:0 0 22px rgba(24,224,168,.28); color:#18E0A8; }
.chs-caret { width:2px; height:22px; background:#18E0A8; animation:chsCaret 1s step-end infinite; }
.chs-cell.big .chs-caret { width:3px; height:40px; }

.chs-phone-row { display:flex; gap:8px; margin-top:18px; }
.chs-country { display:flex; align-items:center; gap:6px; background:#1c1c1c; border:1px solid #2a2a2a;
  border-radius:12px; padding:13px 12px; font-size:14px; font-weight:800; color:#fff; flex:none; }
.chs-phone-input { flex:1; min-width:0; background:#1c1c1c; border:2px solid #18E0A8; border-radius:12px;
  padding:12px 13px; font-size:15px; font-weight:700; color:#fff; font-family:inherit; outline:none; }
.chs-phone-input::placeholder { color:#5f6b73; }
.chs-consent { font-size:10.5px; color:#7f8b93; font-weight:600; line-height:1.5; margin-top:12px; }

.chs-btn-row { display:flex; gap:10px; margin-top:16px; }
.chs-btn-row .chs-cta { flex:1; margin-top:0; }
.chs-cta { display:block; width:100%; margin-top:16px; padding:15px; border:none; border-radius:26px;
  background:#18E0A8; color:#053a2c; font-size:15px; font-weight:900; text-align:center; cursor:pointer;
  font-family:inherit; box-shadow:0 10px 26px rgba(24,224,168,.28); }
.chs-cta:disabled { background:#232323; color:#7f8b93; box-shadow:none; cursor:default; }
.chs-ghost { padding:15px 20px; border-radius:26px; background:#1c1c1c; border:1px solid #2a2a2a;
  color:#cfe0ec; font-size:15px; font-weight:800; text-align:center; cursor:pointer; font-family:inherit; }
.chs-link { display:block; width:100%; margin-top:12px; background:none; border:none; text-align:center;
  font-size:14px; font-weight:800; color:#18E0A8; cursor:pointer; font-family:inherit; padding:6px; }

.chs-code-hero { margin-top:18px; border-radius:16px; overflow:hidden; background:#101010;
  border:1px solid #2a2340; }
.chs-spectrum { height:3px; background:linear-gradient(90deg,#7C36E6,#FF3D86,#FF7A3C,#18E0A8); }
.chs-code-hero-code { padding:18px; text-align:center; font-size:40px; font-weight:900;
  letter-spacing:10px; color:#18E0A8; }

@keyframes chsFade { from { opacity:0; } to { opacity:1; } }
@keyframes chsUp { from { transform:translateY(100%); } to { transform:none; } }
@keyframes chsDown { from { transform:translateY(-100%); } to { transform:none; } }
@keyframes chsCaret { 0%,49% { opacity:1; } 50%,100% { opacity:0; } }
@keyframes chsPop { 0% { transform:scale(.5); opacity:0; } 70% { transform:scale(1.12); opacity:1; }
  100% { transform:scale(1); } }
`;
